import { useState } from 'react';
import { Link, useParams } from 'react-router';
import {
  PAYMENT_RAIL_LABELS,
  type CustomerAction,
  type Order,
  type PaymentRail,
} from '@gatecheckpro/contracts';
import { ApiRefusal } from '@gatecheckpro/api-client';
import {
  Button,
  Card,
  EmptyState,
  LoadingRegion,
  Notice,
  ORDER_STATES,
  Status,
  formatCountdown,
  formatDateInZone,
  formatMoney,
  formatMoneyOrFree,
  hasLapsed,
  presentation,
} from '@gatecheckpro/design-system';
import { Chrome } from './Chrome.tsx';
import { useResource } from '../lib/useResource.ts';
import { useCommand } from '../lib/useCommand.ts';
import { useNowTicking } from '../lib/useCountdown.ts';
import { FailureNotice } from '../lib/refusal.tsx';

/** What `POST /pay` hands back: somewhere to go, or something to do. */
interface InitiatedPayment {
  readonly paymentAttemptId: string;
  readonly status: string;
  readonly rail: PaymentRail;
  readonly customerAction: CustomerAction;
}

/**
 * What the customer has to do now, rendered per variant.
 *
 * Written as an exhaustive switch rather than "show the URL if there is one",
 * because the first version of this page assumed every payment ends in a
 * redirect and neither rail GateCheck ships does. A customer holding a USSD
 * instruction and being shown nothing is the failure this shape prevents.
 */
function CustomerActionPanel({ action }: { action: CustomerAction }) {
  switch (action.type) {
    case 'PAYMENT_INSTRUCTION':
      return (
        <Notice tone="info" title="Finish paying from your phone">
          <div className="gc-stack gc-stack--tight">
            {/* The provider's own words. Paraphrasing a dial string is how
                somebody ends up dialling something that does not work. */}
            <strong className="gc-mono gc-instruction">{action.instruction}</strong>
            <span className="gc-muted">
              Dial it from the phone number registered to your bank account.
            </span>
          </div>
        </Notice>
      );
    case 'BANK_TRANSFER_DETAILS':
      return (
        <Notice tone="info" title="Transfer to this account">
          <div className="gc-stack gc-stack--tight">
            <div className="gc-summary__row">
              <span>Bank</span>
              <strong>{action.bankName}</strong>
            </div>
            <div className="gc-summary__row">
              <span>Account number</span>
              <strong className="gc-mono">{action.accountNumber}</strong>
            </div>
            <div className="gc-summary__row">
              <span>Amount</span>
              <strong>{formatMoney(action.amount)}</strong>
            </div>
            {action.expiresAt !== null && (
              <span className="gc-muted">
                This account works until {new Date(action.expiresAt).toLocaleTimeString()} and then
                stops. It is for this order only — do not save it.
              </span>
            )}
          </div>
        </Notice>
      );
    case 'REDIRECT':
      return (
        <a className="gc-button gc-button--primary" href={action.url}>
          Continue your payment
        </a>
      );
    case 'NONE':
      return (
        <Notice tone="caution" title="Waiting on your bank">
          Nothing more to do. We are checking with the provider.
        </Notice>
      );
  }
}

/**
 * The order: what was reserved, on exactly what terms, and what happens next.
 *
 * Everything shown comes from the frozen quote, so a price the organizer changed
 * a minute ago does not change what this customer owes.
 *
 * Three decisions carry most of the honesty here.
 *
 * The countdown renders the *server's* expiry and, when it reaches zero,
 * reloads rather than concluding anything — a browser with a wrong clock must
 * not tell somebody their tickets are gone.
 *
 * The pay button exists only when the server says this order can actually be
 * paid. Two separate reasons can withhold it — no provider is connected, or
 * GateCheckPro will not sell this security mode — and the page states which,
 * because they have different remedies.
 *
 * And coming back from the provider proves nothing. The return route asks the
 * server what happened; it never reads a status out of a redirect.
 */
export function OrderPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const { resource, reload } = useResource<Order>(
    orderId === undefined ? null : `/api/v1/customer/orders/${orderId}`,
  );
  const abandon = useCommand<Order>();
  const pay = useCommand<InitiatedPayment>();
  const refresh = useCommand<Order>();
  const now = useNowTicking();
  const banks = useResource<{ banks: Array<{ code: string; name: string }> }>(
    '/api/v1/customer/payment-banks',
  );
  const [rail, setRail] = useState<PaymentRail>('USSD');
  const [bankCode, setBankCode] = useState('');

  if (resource.status === 'loading') {
    return (
      <Chrome>
        <main className="gc-main">
          <div className="gc-container">
            <LoadingRegion label="Loading your order" />
          </div>
        </main>
      </Chrome>
    );
  }

  if (resource.status === 'failed') {
    const missing = resource.error instanceof ApiRefusal && resource.error.status === 404;
    return (
      <Chrome>
        <main className="gc-main">
          <div className="gc-container">
            {missing ? (
              <EmptyState
                title="No such order"
                action={
                  <Link to="/orders" className="gc-button gc-button--primary">
                    Your orders
                  </Link>
                }
              >
                This order does not exist, or it belongs to somebody else.
              </EmptyState>
            ) : (
              <FailureNotice error={resource.error} />
            )}
          </div>
        </main>
      </Chrome>
    );
  }

  const order = resource.data;
  const state = presentation(ORDER_STATES, order.status);
  const holding = order.holdExpiresAt !== null;
  const lapsedLocally = holding && hasLapsed(order.holdExpiresAt as string, now);
  const remaining = holding ? formatCountdown(order.holdExpiresAt as string, now) : null;
  const urgent = holding && new Date(order.holdExpiresAt as string).getTime() - now < 2 * 60_000;

  const intent = order.paymentIntent;
  /*
   * Settled, which is not the same as paid.
   *
   * Everything this flag drives — no pay button, no amount due, the link to the
   * tickets — is about the order being finished, and a free order is as
   * finished as a paid one. The two are only told apart where the words
   * actually differ, below.
   */
  const settled =
    order.status === 'PAID_PENDING_ISSUANCE' ||
    order.status === 'ZERO_TOTAL_PENDING_ISSUANCE' ||
    order.status === 'TICKETS_ISSUED' ||
    order.status === 'COMPLETED';
  /*
   * Whether this order ever owed anything — a fact about the order, not a
   * moment in its life.
   *
   * `ZERO_TOTAL_PENDING_ISSUANCE` was the obvious signal and the wrong one: a
   * free order is issued within the checkout request, so this page almost never
   * sees that status, and the wording fell through to "Payment confirmed" for
   * an order where no money moved. The server states the requirement instead,
   * and it is true whenever anybody looks.
   */
  const nothingWasOwed = order.paymentRequirement === 'NOT_REQUIRED';
  const action = intent?.attempt?.customerAction ?? { type: 'NONE' as const };
  const awaitingProvider =
    !settled &&
    intent?.attempt != null &&
    ['REQUIRES_CUSTOMER_ACTION', 'PROVIDER_PENDING', 'VERIFYING'].includes(intent.attempt.status);
  // Offered only when the server says this order can be paid *and* that
  // GateCheckPro is willing to sell it. Guessing either would eventually render
  // a button the server is about to refuse. No intent is the clearest possible
  // version of "there is nothing to pay here".
  const canPay =
    intent !== null &&
    order.status === 'PAYMENT_PENDING' &&
    intent.paymentProviderConnected &&
    intent.fulfillable &&
    !awaitingProvider &&
    holding &&
    !lapsedLocally;
  const paymentTitle = nothingWasOwed
    ? 'Nothing to pay'
    : settled
      ? 'Payment confirmed'
      : awaitingProvider
        ? 'Waiting on the provider'
        : canPay
          ? 'Nothing has been charged yet'
          : 'This order cannot be paid';

  return (
    <Chrome>
      <main className="gc-main">
        <div className="gc-container gc-stack gc-stack--loose" style={{ maxWidth: '46rem' }}>
          <div className="gc-stack gc-stack--tight">
            <span className="gc-eyebrow">Order {order.orderNumber}</span>
            <div className="gc-row gc-row--baseline">
              <h1>{order.event.title}</h1>
              <Status
                tone={state.tone}
                glyph={state.glyph}
                label={state.label}
                title={state.meaning}
              />
            </div>
            {order.event.venueName !== null && <p className="gc-muted">{order.event.venueName}</p>}
            {order.event.sessions[0] !== undefined && (
              <p className="gc-muted">
                {formatDateInZone(order.event.sessions[0].startsAt, order.event.timezone)}
              </p>
            )}
          </div>

          {holding && !lapsedLocally && (
            <div className="gc-row">
              <span className={urgent ? 'gc-countdown gc-countdown--urgent' : 'gc-countdown'}>
                Held for
                <span className="gc-countdown__value">{remaining}</span>
              </span>
            </div>
          )}

          {lapsedLocally && (
            // Looks lapsed here. The server decides, so ask it rather than
            // telling the customer something this page cannot know.
            <Notice tone="caution" title="Your reservation may have run out">
              <div className="gc-stack gc-stack--tight">
                <span>Checking with GateCheck for the current state of this order.</span>
                <Button onClick={reload}>Refresh</Button>
              </div>
            </Notice>
          )}

          <FailureNotice error={abandon.error} />

          <Card title="What you reserved" eyebrow="These terms are fixed">
            <div className="gc-stack">
              {order.quote.lines.map((line) => (
                <div key={line.ticketProductId} className="gc-stack gc-stack--tight">
                  <div className="gc-row gc-row--between">
                    <strong>
                      {line.quantity} × {line.terms.displayName}
                    </strong>
                    <span className="gc-summary__value">
                      {formatMoneyOrFree(line.lineSubtotal)}
                    </span>
                  </div>
                  {line.terms.description !== null && (
                    <span className="gc-muted">{line.terms.description}</span>
                  )}
                  <span className="gc-muted">
                    {formatMoney(line.unitPrice)} each ·{' '}
                    {line.terms.accessScope === 'ALL_SESSIONS'
                      ? 'valid for every date'
                      : `valid for ${String(line.terms.sessionIds.length)} date${line.terms.sessionIds.length === 1 ? '' : 's'}`}
                  </span>
                  {line.terms.securityMode === 'SECURE_PASS' && (
                    <span className="gc-muted">
                      This ticket will need the GateCheck app at the gate.
                    </span>
                  )}
                </div>
              ))}

              <hr className="gc-divider" />

              <div className="gc-summary">
                <div className="gc-summary__row">
                  <span>Subtotal</span>
                  <span className="gc-summary__value">{formatMoney(order.quote.subtotal)}</span>
                </div>
                {order.quote.discount.minor > 0 && (
                  <div className="gc-summary__row">
                    <span>Discount</span>
                    <span className="gc-summary__value">−{formatMoney(order.quote.discount)}</span>
                  </div>
                )}
                {order.quote.fees.minor > 0 && (
                  <div className="gc-summary__row">
                    <span>Fees</span>
                    <span className="gc-summary__value">{formatMoney(order.quote.fees)}</span>
                  </div>
                )}
                {order.quote.tax.minor > 0 && (
                  <div className="gc-summary__row">
                    <span>Tax</span>
                    <span className="gc-summary__value">{formatMoney(order.quote.tax)}</span>
                  </div>
                )}
                <div className="gc-summary__row gc-summary__row--total">
                  <span>Total</span>
                  <span className="gc-summary__value">{formatMoney(order.quote.total)}</span>
                </div>
              </div>

              <span className="gc-muted">
                This price is locked to your reservation. If the organizer changes what these
                tickets cost, yours does not change.
              </span>
            </div>
          </Card>

          <Card title="Payment" eyebrow="What happens next">
            <div className="gc-stack">
              <Notice
                tone={settled ? 'positive' : canPay ? 'info' : 'caution'}
                title={paymentTitle}
              >
                {order.paymentMessage}
              </Notice>

              <div className="gc-summary__row">
                {/* A free order neither owes nor paid this. It cost nothing. */}
                <span>{nothingWasOwed ? 'Total' : settled ? 'Amount paid' : 'Amount due'}</span>
                <span className="gc-summary__value">
                  {/* The order's own amount when nothing is owed — there is no
                      obligation to read an expected amount from. */}
                  {formatMoney(intent?.expectedAmount ?? order.amount)}
                </span>
              </div>

              {canPay && (
                <form
                  className="gc-stack"
                  onSubmit={(formEvent) => {
                    formEvent.preventDefault();
                    void pay
                      .run(`/api/v1/customer/orders/${order.orderId}/pay`, {
                        method: 'POST',
                        body: rail === 'USSD' ? { rail, bankCode } : { rail },
                      })
                      .then((result) => {
                        if (result === null) return;
                        // A redirect is one possible outcome, not the outcome.
                        // Both R1 rails return something to do here instead.
                        if (result.customerAction.type === 'REDIRECT') {
                          window.location.assign(result.customerAction.url);
                          return;
                        }
                        reload();
                      });
                  }}
                >
                  <fieldset className="gc-fieldset">
                    <legend className="gc-field__label">How do you want to pay?</legend>
                    {(intent?.availableRails ?? []).map((offered) => (
                      <label key={offered} className="gc-choice">
                        <input
                          type="radio"
                          name="rail"
                          value={offered}
                          checked={rail === offered}
                          onChange={() => setRail(offered)}
                        />
                        <span>
                          <strong>{PAYMENT_RAIL_LABELS[offered]}</strong>
                          <span className="gc-muted">
                            {offered === 'USSD'
                              ? ' — dial a short code from the phone number on your bank account.'
                              : ' — transfer from your bank app to a one-time account.'}
                          </span>
                        </span>
                      </label>
                    ))}
                  </fieldset>

                  {rail === 'USSD' && (
                    <div className="gc-field">
                      <label className="gc-field__label" htmlFor="bankCode">
                        Your bank
                      </label>
                      <select
                        id="bankCode"
                        className="gc-select"
                        required
                        value={bankCode}
                        onChange={(changeEvent) => setBankCode(changeEvent.target.value)}
                      >
                        <option value="">Choose your bank</option>
                        {banks.resource.status === 'loaded' &&
                          banks.resource.data.banks.map((bank) => (
                            <option key={bank.code} value={bank.code}>
                              {bank.name}
                            </option>
                          ))}
                      </select>
                      <span className="gc-field__hint">
                        GateCheck never asks for your card, PIN or bank password.
                      </span>
                    </div>
                  )}

                  <Button type="submit" variant="primary" busy={pay.busy}>
                    Pay {formatMoney(intent?.expectedAmount ?? order.amount)}
                  </Button>
                  <span className="gc-muted">
                    GateCheck confirms the payment with your provider directly, so your tickets
                    appear once we have checked — not because you came back to this page.
                  </span>
                </form>
              )}

              {awaitingProvider && (
                <div className="gc-stack">
                  <CustomerActionPanel action={action} />
                  <Button
                    busy={refresh.busy}
                    onClick={() => {
                      void refresh
                        .run(`/api/v1/customer/orders/${order.orderId}/refresh-payment`, {
                          method: 'POST',
                        })
                        .then(() => {
                          reload();
                        });
                    }}
                  >
                    I have paid — check with my bank
                  </Button>
                  <span className="gc-muted">
                    We ask the provider ourselves. If they have taken your money, your tickets
                    appear here even if you closed the page.
                  </span>
                </div>
              )}

              {settled && (
                <Link to="/tickets" className="gc-button gc-button--primary">
                  See your tickets
                </Link>
              )}
            </div>
          </Card>

          <FailureNotice error={pay.error} />
          <FailureNotice error={refresh.error} />

          {order.status === 'PAYMENT_PENDING' && holding && !lapsedLocally && (
            <div className="gc-row">
              <Button
                variant="destructive"
                busy={abandon.busy}
                onClick={() =>
                  void abandon
                    .run(`/api/v1/customer/orders/${order.orderId}/abandon`, {
                      method: 'POST',
                      body: { reasonCode: 'CUSTOMER_ABANDONED' },
                    })
                    .then((result) => {
                      if (result !== null) reload();
                    })
                }
              >
                Release these tickets
              </Button>
              <span className="gc-muted">
                Puts them back on sale immediately for somebody else.
              </span>
            </div>
          )}

          {(order.status === 'EXPIRED' || order.status === 'CANCELLED') && (
            <Notice tone="neutral" title={state.label}>
              <div className="gc-stack gc-stack--tight">
                <span>{state.meaning}</span>
                <Link to={`/e/${order.event.slug}`} className="gc-button gc-button--primary">
                  Look at this event again
                </Link>
              </div>
            </Notice>
          )}

          <p className="gc-muted">
            <Link to="/orders">All your orders</Link>
          </p>
        </div>
      </main>
    </Chrome>
  );
}
